import { useNavigate } from 'react-router-dom';
import { LuLogOut, LuMoon, LuStore, LuSun } from 'react-icons/lu';
import { moreRoutes } from '../../routes';
import { useUserContext } from '../../context/userContext';
import { useTheme } from '../../context/themeContext';
import { useTr } from '../../context/langContext';
import LangToggle from '../../components/ui/lang-toggle';
import { ownerStrings } from '../../i18n/owner-strings';
import { webStrings } from '../../i18n/web-strings';

/** Mobile "More" sheet: everything that does not fit in the bottom nav, plus outlet and sign-out. */
export default function MoreSheet({
    open,
    onClose,
    onPickOutlet,
}: {
    open: boolean;
    onClose: () => void;
    onPickOutlet: () => void;
}) {
    const navigate = useNavigate();
    const { outlet, outlets, accountName, signOut } = useUserContext();
    const { isDark, toggle } = useTheme();
    const tr = useTr();

    if (!open) return null;

    const go = (to: string) => {
        onClose();
        navigate(to);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end bg-black/40" onClick={onClose}>
            <div
                className="max-h-[82vh] w-full animate-slide-up overflow-y-auto rounded-t-3xl bg-bg p-4 pb-8"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-line" />

                <button
                    onClick={() => {
                        onClose();
                        onPickOutlet();
                    }}
                    disabled={outlets.length < 2}
                    className="mb-3 flex w-full items-center gap-3 rounded-2xl bg-owner-bg px-4 py-3 text-left"
                >
                    <LuStore className="text-owner-ink" size={18} />
                    <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-bold text-owner-ink">{outlet?.name ?? tr(webStrings, 'outlet')}</p>
                        <p className="truncate text-2xs text-owner-ink/70">{accountName}</p>
                    </div>
                </button>

                <div className="grid grid-cols-2 gap-2">
                    {moreRoutes.map(({ to, name, icon: Icon }) => (
                        <button
                            key={to}
                            onClick={() => go(to)}
                            className="flex h-[70px] flex-col justify-center gap-1.5 rounded-2xl border border-line bg-surf2 px-3 text-left"
                        >
                            <Icon size={17} className="text-owner-ink" />
                            <span className="text-[12.5px] font-semibold leading-tight text-ink">{name(tr)}</span>
                        </button>
                    ))}
                </div>

                <div className="mt-3 flex gap-2">
                    <button
                        onClick={toggle}
                        className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-line text-[13px] font-semibold text-ink2"
                    >
                        {isDark ? <LuSun size={16} /> : <LuMoon size={16} />}
                        {tr(webStrings, isDark ? 'light' : 'dark')}
                    </button>
                    <LangToggle className="h-11 flex-1 justify-center rounded-xl border border-line text-[13px] text-ink2" />
                    <button
                        onClick={signOut}
                        className="flex h-11 flex-1 items-center justify-center gap-2 rounded-xl border border-line text-[13px] font-semibold text-danger"
                    >
                        <LuLogOut size={16} />
                        {tr(ownerStrings, 'signOut')}
                    </button>
                </div>
            </div>
        </div>
    );
}
